import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useQuery } from 'react-query'
import { useAuth as useCrossfitAuth } from '@crossfit/auth'
import { datadogRum } from '@datadog/browser-rum'
import { User } from 'types'
import * as cookiesService from 'services/cookies'
import * as userService from 'services/user'
import CrossfitAuthProvider from './cfAuthContext'
import type { TokenDetails } from '@crossfit/auth/dist/types/oauth/types'
import { trackEvent } from 'services/segment'
import useReturnToPage from 'hooks/useReturnToPage'

type AuthContext = {
  user: User
  isLoading: boolean
  login: (afterLoginCallback?: () => void) => void
  logout: (afterLogoutCallback?: () => void) => void
}

const Context = createContext({} as AuthContext)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { saveLocationToReturnTo } = useReturnToPage()
  const [accessToken, setAccessToken] = useState<string | null>(
    cookiesService.getAccessToken(),
  )

  const userQuery = useQuery('user', userService.fetchUser, {
    enabled: !!accessToken,
    onError: () => {
      logout()
    },
  })

  async function login(afterLogin?: () => void) {
    afterLogin?.()
  }

  function logout(cb?: () => void) {
    trackEvent({
      component: 'Auth',
      event: 'Auth Logout',
      location: 'Affiliate Toolkit',
    })
    setAccessToken(null)
    userQuery.remove()
    cookiesService.removeAccessToken()
    datadogRum.clearUser()
    cb?.()
  }

  const handleRecieveAccessToken = useCallback((tokenDetails: TokenDetails) => {
    cookiesService.setAccessToken(tokenDetails.access_token)
    setAccessToken(tokenDetails.access_token)
  }, [])

  useEffect(() => {
    const user = userQuery.data
    if (user) {
      datadogRum.setUser({ email: user.email, id: String(user.id) })
    }
  }, [userQuery.data])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    saveLocationToReturnTo(`${location.pathname}?${urlParams.toString()}`)
  }, [])

  return (
    <CrossfitAuthProvider handleRecieveAccessToken={handleRecieveAccessToken}>
      <Context.Provider
        value={{
          login,
          logout,
          user: userQuery.data,
          isLoading: userQuery.isLoading,
        }}
      >
        {children}
      </Context.Provider>
    </CrossfitAuthProvider>
  )
}

export function useAuth() {
  const { initiateLogin, initiateLogoutFlow, authenticated, unauthenticated } =
    useCrossfitAuth()
  const authContext = useContext(Context)
  const login = () => authContext.login(initiateLogin)
  const logout = () => authContext.logout(initiateLogoutFlow)
  return { ...authContext, login, logout, authenticated, unauthenticated }
}
